import React from "react";
import DataList from "components/datatable/DataList";
import DataPagination from "components/datatable/DataPagination";
import PopupDialog from "components/dialog/PopupDialog";
import MyForm from "components/form-elements/MyForm";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { useAuth } from "context/AuthProvider";
import { RuleRequired } from "utils/MyUtils";
import { parseBoolean, showEmpty } from "features/kms/system/SystemPageUtils";
import { formatPersonName } from "features/kms/common/nameFormat";

function toArray(resp) {
  return Array.isArray(resp?.data) ? resp.data : [];
}

function kontingenAddConfig() {
  return {
    title: "Tambah Kontingen",
    url: "/references/kontingen",
    cellid: "id_kontingen",
    columns: ["kode", "nama"],
    coltype: {
      kode: {
        type: "text",
        label: "Kode",
        name: "kode",
        validation: RuleRequired,
        colsize: 4,
      },
      nama: {
        type: "text",
        label: "Nama Kontingen",
        name: "nama",
        validation: RuleRequired,
        colsize: 8,
      },
    },
  };
}

const compactNoCol = { label: "No", width: 35, pdfwidth: 5, align: "center" };
const compactActionCol = { label: "Aksi", align: "center", width: 48 };
const allowDeleteWhenSafe = (item) => ["true", "t", "1"].includes(String(item?.can_delete).toLowerCase());

function PelatihBinaanDialog({ show, item, closeHandle, onSaved }) {
  const [, , loading, axiosFetch] = useAxiosFunction();
  const [showMsg] = useMsgBox();
  const showMsgRef = React.useRef(showMsg);
  const [payload, setPayload] = React.useState({ pelatih: {} });
  const [activeTab, setActiveTab] = React.useState("atlet");
  const [rows, setRows] = React.useState([]);
  const [paging, setPaging] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [keyword, setKeyword] = React.useState("");
  const [sortCol, setSortCol] = React.useState("");
  const [sortDir, setSortDir] = React.useState("asc");
  const [atletIDs, setAtletIDs] = React.useState(new Set());
  const [timIDs, setTimIDs] = React.useState(new Set());
  const atletIDsRef = React.useRef(atletIDs);
  const timIDsRef = React.useRef(timIDs);

  React.useEffect(() => {
    showMsgRef.current = showMsg;
  }, [showMsg]);

  React.useEffect(() => {
    atletIDsRef.current = atletIDs;
  }, [atletIDs]);

  React.useEffect(() => {
    timIDsRef.current = timIDs;
  }, [timIDs]);

  React.useEffect(() => {
    if (show) {
      const loadData = async () => {
        if (!item?.id_kejuaraan_sdm) return;
        const resp = await axiosFetch({ method: "GET", url: `/kejuaraan-admin/pelatih-binaan/${item.id_kejuaraan_sdm}`, auth: true });
        if (resp?.data) {
          const nextPayload = {
            pelatih: resp.data?.pelatih || {},
          };
          setPayload(nextPayload);
          setAtletIDs(new Set(Array.isArray(resp.data?.selected_atlet_ids) ? resp.data.selected_atlet_ids.map(String) : []));
          setTimIDs(new Set(Array.isArray(resp.data?.selected_tim_ids) ? resp.data.selected_tim_ids.map(String) : []));
        } else {
          await showMsgRef.current("Error", resp?.message || "Data binaan pelatih gagal dimuat");
        }
      };
      setPayload({ pelatih: {} });
      setRows([]);
      setPaging(null);
      setActiveTab("atlet");
      setPage(1);
      setKeyword("");
      setSortCol("");
      setSortDir("asc");
      loadData();
    } else {
      setPayload({ pelatih: {} });
      setRows([]);
      setPaging(null);
      setAtletIDs(new Set());
      setTimIDs(new Set());
    }
  }, [axiosFetch, item?.id_kejuaraan_sdm, show]);

  React.useEffect(() => {
    if (!show || !item?.id_kejuaraan_sdm) return;
    const loadOptions = async () => {
      const resp = await axiosFetch({
        method: "GET",
        url: `/kejuaraan-admin/pelatih-binaan/${item.id_kejuaraan_sdm}/options`,
        auth: true,
        params: {
          target: activeTab,
          page,
          q: keyword,
          sort: sortCol ? `${sortCol} ${sortDir}` : "",
        },
      });
      setRows(Array.isArray(resp?.data) ? resp.data : []);
      setPaging(resp?.page || null);
    };
    loadOptions();
  }, [activeTab, axiosFetch, item?.id_kejuaraan_sdm, keyword, page, show, sortCol, sortDir]);

  const toggleSet = (setter, id) => {
    setter((current) => {
      const next = new Set(current);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedSet = activeTab === "tim" ? timIDs : atletIDs;
  const selectedCount = activeTab === "tim" ? timIDs.size : atletIDs.size;
  const sortData = (name) => {
    setPage(1);
    if (sortCol === name) {
      setSortDir((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }
    setSortCol(name);
    setSortDir("asc");
  };
  const sortClass = (name) => {
    if (sortCol !== name) return "sorting";
    return `sorting sorting_${sortDir}`;
  };
  const changeTab = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setKeyword("");
    setSortCol("");
    setSortDir("asc");
  };

  const saveData = async () => {
    const resp = await axiosFetch({
      method: "POST",
      url: `/kejuaraan-admin/pelatih-binaan/${item?.id_kejuaraan_sdm}`,
      auth: true,
      data: {
        atlet_ids: Array.from(atletIDsRef.current),
        tim_ids: Array.from(timIDsRef.current),
      },
    });
    if (resp?.data?.success === true) {
      await showMsg("Informasi", "Atlet/Tim Binaan berhasil disimpan.");
      if (onSaved) onSaved();
      closeHandle();
    } else {
      await showMsg("Error", resp?.message || "Atlet/Tim Binaan gagal disimpan");
    }
  };

  return (
    <PopupDialog title="Atur Atlet/Tim Binaan Pelatih" size="xl" show={show} closeHandle={closeHandle} allowClose={false}>
      <div className="p-2">
        <div className="alert alert-info py-2">
          Pilih atlet atau tim yang benar-benar menjadi binaan pelatih. Jika tidak ada pilihan yang disimpan, pelatih tidak dihitung dalam pencatatan medali.
        </div>
        <div className="mb-3">
          <div><strong>Pelatih:</strong> {showEmpty(formatPersonName(payload?.pelatih || item, ["pelatih_name", "personal_name"]))}</div>
          <div><strong>Cabor:</strong> {showEmpty(payload?.pelatih?.cabor_name || item?.cabor_name)}</div>
        </div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button type="button" className={`nav-link px-4 ${activeTab === "atlet" ? "active" : ""}`} onClick={() => changeTab("atlet")}>
                Atlet <span className="badge badge-primary ml-1">{atletIDs.size}</span>
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link px-4 ${activeTab === "tim" ? "active" : ""}`} onClick={() => changeTab("tim")}>
                Tim <span className="badge badge-primary ml-1">{timIDs.size}</span>
              </button>
            </li>
          </ul>
          <div className="text-muted small">Terpilih: {selectedCount}</div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder={activeTab === "tim" ? "Cari tim atau nomor pertandingan..." : "Cari NIK, No. Induk, nama atlet, atau nomor pertandingan..."}
              value={keyword}
              onChange={(event) => {
                setKeyword(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
        <div className="dataTables_wrapper dt-bootstrap4">
          <div className="row">
            <div className="col-12 table-responsive">
              <table className="table sortable table-bordered table-hover table-striped table-condensed">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: 70 }}>Pilih</th>
                    {activeTab === "tim" ? (
                      <>
                        <th className={sortClass("nama")} onClick={() => sortData("nama")}>Tim</th>
                        <th className={sortClass("nomor_pertandingan_name")} onClick={() => sortData("nomor_pertandingan_name")}>Nomor Pertandingan</th>
                      </>
                    ) : (
                      <>
                        <th className={sortClass("nik")} onClick={() => sortData("nik")} style={{ width: 130 }}>NIK</th>
                        <th className={sortClass("nomor_induk")} onClick={() => sortData("nomor_induk")} style={{ width: 110 }}>No. Induk</th>
                        <th className={sortClass("nama")} onClick={() => sortData("nama")}>Nama Atlet</th>
                        <th className={sortClass("nomor_pertandingan_name")} onClick={() => sortData("nomor_pertandingan_name")}>Nomor Pertandingan</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.length < 1 ? (
                    <tr>
                      <td colSpan={activeTab === "tim" ? 3 : 5} className="text-center text-muted">Data tidak tersedia</td>
                    </tr>
                  ) : rows.map((row) => {
                    const id = String(row.id);
                    const checked = selectedSet.has(id);
                    return (
                      <tr key={id}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => activeTab === "tim" ? toggleSet(setTimIDs, id) : toggleSet(setAtletIDs, id)}
                          />
                        </td>
                        {activeTab === "tim" ? (
                          <>
                            <td>{showEmpty(row.nama)}</td>
                            <td>{showEmpty(row.nomor_pertandingan_name)}</td>
                          </>
                        ) : (
                          <>
                            <td className="text-center">{showEmpty(row.nik)}</td>
                            <td className="text-center">{showEmpty(row.nomor_induk)}</td>
                            <td>{showEmpty(row.nama)}</td>
                            <td>{showEmpty(row.nomor_pertandingan_name)}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DataPagination paging={paging} changePage={setPage} />
        </div>
        <div className="text-right mt-3">
          <button type="button" className="btn btn-default mr-2" onClick={closeHandle} disabled={loading}>Tutup</button>
          <button type="button" className="btn btn-primary" onClick={saveData} disabled={loading}>
            {loading ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </PopupDialog>
  );
}

function checklistColumnStyle(col) {
  if (!col?.width) return undefined;
  const width = typeof col.width === "number" ? `${col.width}px` : col.width;
  return {
    width,
    minWidth: width,
    whiteSpace: col.wrap ? "normal" : "nowrap",
  };
}

function parseCSV(value) {
  if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseOptionList(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

function parseObject(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    return {};
  }
}

function responsePayload(resp) {
  if (resp?.data && typeof resp.data === "object") return resp.data;
  if (resp && typeof resp === "object" && resp?.data !== undefined) return resp.data;
  return resp;
}

function isCanceledResponse(resp) {
  const message = String(resp?.message || resp?.error || "").toLowerCase();
  return message === "canceled" || message === "cancelled" || message.includes("canceled");
}

function isNomorPerorangan(nomor) {
  return String(nomor?.kategori || "").trim().toLowerCase() === "perorangan";
}

function getNomorLabel(nomor) {
  return String(nomor?.label || "").trim();
}

function getTeamLabel(team, nomor) {
  const teamName = String(team?.nama || "").trim();
  const nomorLabel = getNomorLabel(nomor);
  if (!teamName) return "-";
  return nomorLabel ? `${teamName} - ${nomorLabel}` : teamName;
}

function BulkChecklistDialog({
  show,
  title,
  optionsUrl,
  saveUrl,
  columns,
  filters = [],
  addConfig = null,
  addSaveHandle = null,
  koniLabel = "",
  closeHandle,
  onSaved,
}) {
  const [showMsg] = useMsgBox();
  const [, , loading, axiosFetch] = useAxiosFunction();
  const [rows, setRows] = React.useState([]);
  const [optionRows, setOptionRows] = React.useState([]);
  const [paging, setPaging] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [sortCol, setSortCol] = React.useState("");
  const [sortDir, setSortDir] = React.useState("asc");
  const [keyword, setKeyword] = React.useState("");
  const [filterValue, setFilterValue] = React.useState({});
  const [selected, setSelected] = React.useState(new Set());
  const [lockedSelected, setLockedSelected] = React.useState(new Set());
  const [selectedMulti, setSelectedMulti] = React.useState({});
  const [selectedTeams, setSelectedTeams] = React.useState({});
  const [selectedTargets, setSelectedTargets] = React.useState({});
  const [selectedPelatihs, setSelectedPelatihs] = React.useState({});
  const [multiPicker, setMultiPicker] = React.useState(null);
  const [multiKeyword, setMultiKeyword] = React.useState("");
  const [multiPage, setMultiPage] = React.useState(1);
  const [koniSendiriID, setKoniSendiriID] = React.useState("");
  const [addOpen, setAddOpen] = React.useState(false);
  const [teamForm, setTeamForm] = React.useState(null);
  const columnsRef = React.useRef(columns);
  const selectedMultiRef = React.useRef(selectedMulti);
  const selectedTeamsRef = React.useRef(selectedTeams);
  const selectedTargetsRef = React.useRef(selectedTargets);
  const selectedPelatihsRef = React.useRef(selectedPelatihs);
  const optionRowsRef = React.useRef(optionRows);

  React.useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  React.useEffect(() => {
    selectedMultiRef.current = selectedMulti;
  }, [selectedMulti]);

  React.useEffect(() => {
    selectedTeamsRef.current = selectedTeams;
  }, [selectedTeams]);

  React.useEffect(() => {
    selectedTargetsRef.current = selectedTargets;
  }, [selectedTargets]);

  React.useEffect(() => {
    selectedPelatihsRef.current = selectedPelatihs;
  }, [selectedPelatihs]);

  React.useEffect(() => {
    optionRowsRef.current = optionRows;
  }, [optionRows]);

  const loadPage = React.useCallback(async () => {
    if (!show) return;
    const resp = await axiosFetch({
      method: "GET",
      url: optionsUrl,
      auth: true,
      params: { page, q: keyword, sort: sortCol ? `${sortCol} ${sortDir}` : "", ...filterValue },
    });
    const dataRows = Array.isArray(resp?.data) ? resp.data : [];
    setRows(dataRows);
    setOptionRows((current) => {
      const next = [...current];
      dataRows.forEach((item) => {
        const idx = next.findIndex((row) => String(row.id) === String(item.id));
        if (idx >= 0) {
          next[idx] = item;
        } else {
          next.push(item);
        }
      });
      optionRowsRef.current = next;
      return next;
    });
    const pageSelectedIDs = dataRows.filter((item) => parseBoolean(item?.selected)).map((item) => String(item.id));
    if (pageSelectedIDs.length > 0) {
      setSelected((current) => {
        const next = new Set(current);
        pageSelectedIDs.forEach((id) => next.add(id));
        return next;
      });
      setLockedSelected((current) => {
        const next = new Set(current);
        pageSelectedIDs.forEach((id) => next.add(id));
        return next;
      });
    }
    const columnsWithMulti = columnsRef.current.filter((col) => col.type === "multi_select");
    if (columnsWithMulti.length > 0) {
      setSelectedMulti((current) => {
        const next = { ...current };
        dataRows.forEach((item) => {
          const key = String(item.id);
          columnsWithMulti.forEach((col) => {
            if (parseBoolean(item?.selected)) {
              next[`${key}:${col.name}`] = new Set(parseCSV(item?.[col.selectedField]));
            }
          });
        });
        selectedMultiRef.current = next;
        return next;
      });
    }
    const columnsWithTeam = columnsRef.current.filter((col) => col.type === "team_select");
    if (columnsWithTeam.length > 0) {
      setSelectedTeams((current) => {
        const next = { ...current };
        dataRows.forEach((item) => {
          if (!parseBoolean(item?.selected)) return;
          const rowKey = String(item.id);
          columnsWithTeam.forEach((col) => {
            const selectedMap = parseObject(item?.[col.selectedField]);
            Object.keys(selectedMap).forEach((nomorID) => {
              const idTim = String(selectedMap[nomorID] || "");
              if (idTim) next[`${rowKey}:${col.multiColumnName}:${nomorID}`] = idTim;
            });
          });
        });
        selectedTeamsRef.current = next;
        return next;
      });
    }
    const columnsWithMeta = columnsRef.current.filter((col) => col.type === "multi_select" && col.enableTargetPelatih);
    if (columnsWithMeta.length > 0) {
      setSelectedTargets((current) => {
        const next = { ...current };
        dataRows.forEach((item) => {
          if (!parseBoolean(item?.selected)) return;
          const rowKey = String(item.id);
          columnsWithMeta.forEach((col) => {
            const selectedMap = parseObject(item?.[col.selectedTargetField]);
            Object.keys(selectedMap).forEach((nomorID) => {
              const idMedali = String(selectedMap[nomorID] || "");
              if (idMedali) next[`${rowKey}:${col.name}:${nomorID}`] = idMedali;
            });
          });
        });
        selectedTargetsRef.current = next;
        return next;
      });
      setSelectedPelatihs((current) => {
        const next = { ...current };
        dataRows.forEach((item) => {
          if (!parseBoolean(item?.selected)) return;
          const rowKey = String(item.id);
          columnsWithMeta.forEach((col) => {
            const selectedMap = parseObject(item?.[col.selectedPelatihField]);
            Object.keys(selectedMap).forEach((nomorID) => {
              const idPelatih = String(selectedMap[nomorID] || "");
              if (idPelatih) next[`${rowKey}:${col.name}:${nomorID}`] = idPelatih;
            });
          });
        });
        selectedPelatihsRef.current = next;
        return next;
      });
    }
    const koniRow = dataRows.find((item) => parseBoolean(item?.is_koni_sendiri));
    if (koniRow?.id) {
      setKoniSendiriID(String(koniRow.id));
    }
    setPaging(resp?.page || null);
  }, [axiosFetch, filterValue, keyword, optionsUrl, page, show, sortCol, sortDir]);

  React.useEffect(() => {
    if (!show) return;
    setPage(1);
    setSortCol("");
    setSortDir("asc");
    setKeyword("");
    setFilterValue({});
    setSelected(new Set());
    setLockedSelected(new Set());
    setOptionRows([]);
    optionRowsRef.current = [];
    setSelectedMulti({});
    setSelectedTeams({});
    setSelectedTargets({});
    setSelectedPelatihs({});
    selectedMultiRef.current = {};
    selectedTeamsRef.current = {};
    selectedTargetsRef.current = {};
    selectedPelatihsRef.current = {};
    setMultiPicker(null);
    setMultiKeyword("");
    setMultiPage(1);
    setKoniSendiriID("");
    setTeamForm(null);
  }, [show]);

  React.useEffect(() => {
    loadPage();
  }, [loadPage]);

  const toggleID = (id) => {
    setSelected((current) => {
      const next = new Set(current);
      const key = String(id);
      if (lockedSelected.has(key)) {
        return next;
      }
      if (next.has(key)) {
        next.delete(key);
        if (koniSendiriID === key) setKoniSendiriID("");
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const chooseKoniSendiri = (id) => {
    const key = String(id);
    setSelected((current) => {
      const next = new Set(current);
      next.add(key);
      return next;
    });
    setKoniSendiriID(key);
  };

  const toggleMultiValue = (rowID, columnName, value) => {
    const stateKey = `${rowID}:${columnName}`;
    const optionKey = String(value);
    const current = selectedMultiRef.current || {};
    const currentSet = current[stateKey] || new Set();
    const nextSet = new Set(currentSet);
	      if (nextSet.has(optionKey)) {
	      nextSet.delete(optionKey);
	      const latestTargets = { ...(selectedTargetsRef.current || {}) };
	      const latestPelatihs = { ...(selectedPelatihsRef.current || {}) };
	      delete latestTargets[`${rowID}:${columnName}:${optionKey}`];
	      delete latestPelatihs[`${rowID}:${columnName}:${optionKey}`];
	      selectedTargetsRef.current = latestTargets;
	      selectedPelatihsRef.current = latestPelatihs;
	      setSelectedTargets(latestTargets);
	      setSelectedPelatihs(latestPelatihs);
	      const teamColumns = columnsRef.current.filter((col) => col.type === "team_select" && col.multiColumnName === columnName);
      if (teamColumns.length > 0) {
        const latestTeams = { ...(selectedTeamsRef.current || {}) };
        teamColumns.forEach(() => {
          delete latestTeams[`${rowID}:${columnName}:${optionKey}`];
        });
        selectedTeamsRef.current = latestTeams;
        setSelectedTeams(latestTeams);
      }
    } else {
      nextSet.add(optionKey);
    }
    const next = { ...current, [stateKey]: nextSet };
    selectedMultiRef.current = next;
    setSelectedMulti(next);
  };

  const selectedMultiLabels = (item, col) => {
    const rowID = String(item.id);
    const stateKey = `${rowID}:${col.name}`;
    const values = selectedMulti[stateKey] || new Set();
    if (values.size < 1) return "";
    const options = parseOptionList(item?.[col.optionsField]);
    return options
      .filter((option) => values.has(String(option?.id || "")))
      .map((option) => option?.label)
      .filter(Boolean)
      .join(", ");
  };

  const renderMultiButton = (item, col) => {
    const rowID = String(item.id);
    const options = parseOptionList(item?.[col.optionsField]);
    const stateKey = `${rowID}:${col.name}`;
    const values = selectedMulti[stateKey] || new Set();
    const disabled = lockedSelected.has(rowID) || !selected.has(rowID) || options.length < 1;
    const count = values.size;
    const labels = selectedMultiLabels(item, col);
    return (
      <div className="d-flex align-items-center">
        <button
          type="button"
          className={`btn btn-xs ${count > 0 ? "btn-primary" : "btn-outline-primary"} flex-shrink-0`}
          disabled={disabled}
          onClick={() => openMultiPicker(item, col)}
        >
          <i className="fa fa-check-square mr-1" />
          Pilih Nomor
        </button>
        {labels ? <span className="ml-2">{labels}</span> : null}
      </div>
    );
  };

  const renderTeamSelect = (item, col) => {
    const rowID = String(item.id);
    if (!selected.has(rowID)) return "-";
    const stateKey = `${rowID}:${col.multiColumnName}`;
    const nomorIDs = Array.from(selectedMulti[stateKey] || []);
    if (nomorIDs.length < 1) return <span className="text-muted">Pilih nomor dulu</span>;
    const nomorOptions = parseOptionList(item?.[col.optionsField]);
    const selectedNomorOptions = nomorIDs
      .map((nomorID) => nomorOptions.find((option) => String(option?.id || "") === String(nomorID)))
      .filter(Boolean)
      .filter((nomor) => !isNomorPerorangan(nomor));
    if (selectedNomorOptions.length < 1) return "";
    return (
      <div>
        {selectedNomorOptions.map((nomor, index) => {
          const nomorID = String(nomor?.id || "");
          const nomorLabel = getNomorLabel(nomor);
          const teamOptions = Array.isArray(nomor?.team_options) ? nomor.team_options : [];
          const teamKey = `${rowID}:${col.multiColumnName}:${nomorID}`;
          return (
            <div className="mb-2" key={teamKey}>
              <div className="small text-muted mb-1">{`Tim ${index + 1}${nomorLabel ? ` - ${nomorLabel}` : ""}`}</div>
              <select
                className="form-control form-control-sm"
                value={selectedTeams[teamKey] || ""}
                disabled={lockedSelected.has(rowID)}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "__ADD_TEAM__") {
                    setTeamForm({
                      rowID,
                      columnName: col.multiColumnName,
                      nomorID,
                      idCabor: item?.id_cabor || "",
                      nomorLabel: nomor?.label || "",
                      nama: "",
                    });
                    return;
                  }
                  const latest = { ...(selectedTeamsRef.current || {}) };
                  if (value) {
                    latest[teamKey] = value;
                  } else {
                    delete latest[teamKey];
                  }
                  selectedTeamsRef.current = latest;
                  setSelectedTeams(latest);
                }}
              >
                <option value="">{nomorLabel ? `-Pilih Tim ${nomorLabel}-` : "-Pilih Tim-"}</option>
                {teamOptions.map((team) => (
                  <option key={team?.id} value={team?.id}>{getTeamLabel(team, nomor)}</option>
                ))}
                <option value="__ADD_TEAM__">Lainnya</option>
              </select>
            </div>
          );
        })}
      </div>
    );
  };

  const openMultiPicker = (item, col) => {
    setMultiKeyword("");
    setMultiPage(1);
    setMultiPicker({ item, col });
  };

  const closeMultiPicker = () => {
    setMultiKeyword("");
    setMultiPage(1);
    setMultiPicker(null);
  };

  const sortData = (col) => {
    if (col?.sortable === false || ["multi_select", "team_select"].includes(col?.type)) return;
    setPage(1);
    if (sortCol === col.name) {
      setSortDir((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }
    setSortCol(col.name);
    setSortDir("asc");
  };

  const sortClass = (col) => {
    if (col?.sortable === false || ["multi_select", "team_select"].includes(col?.type)) return "";
    if (sortCol !== col.name) return "sorting";
    return `sorting sorting_${sortDir}`;
  };

  const save = async () => {
    const multiColumns = columns.filter((col) => col.type === "multi_select");
    const teamColumns = columns.filter((col) => col.type === "team_select");
    const latestMulti = selectedMultiRef.current || {};
    const latestTeams = selectedTeamsRef.current || {};
    const latestTargets = selectedTargetsRef.current || {};
    const latestPelatihs = selectedPelatihsRef.current || {};
    const newlySelected = Array.from(selected).filter((id) => !lockedSelected.has(String(id)));
    if (multiColumns.length > 0) {
      for (const id of newlySelected) {
        for (const col of multiColumns) {
          const values = Array.from(latestMulti[`${id}:${col.name}`] || []);
          if (col.required && values.length < 1) {
            await showMsg("Error", `${col.label} wajib diisi.`);
            return;
          }
        }
      }
    }
    for (const id of newlySelected) {
      for (const col of teamColumns) {
        const values = Array.from(latestMulti[`${id}:${col.multiColumnName}`] || []);
        const row = rows.find((item) => String(item.id) === String(id))
          || (optionRowsRef.current || []).find((item) => String(item.id) === String(id));
        const options = parseOptionList(row?.[col.optionsField]);
        for (const nomorID of values) {
          const nomor = options.find((option) => String(option?.id || "") === String(nomorID));
          if (!isNomorPerorangan(nomor) && !latestTeams[`${id}:${col.multiColumnName}:${nomorID}`]) {
            await showMsg("Error", "Tim wajib diisi.");
            return;
          }
        }
      }
    }
    const items = newlySelected.map((id) => {
      const item = { id };
      multiColumns.forEach((col) => {
        const values = Array.from(latestMulti[`${id}:${col.name}`] || []);
        item[col.payloadName || col.name] = values;
        if (col.enableTargetPelatih) {
          const targetMap = {};
          const pelatihMap = {};
          values.forEach((nomorID) => {
            const idMedali = latestTargets[`${id}:${col.name}:${nomorID}`];
            const idPelatih = latestPelatihs[`${id}:${col.name}:${nomorID}`];
            if (idMedali) targetMap[nomorID] = Number(idMedali);
            if (idPelatih) pelatihMap[nomorID] = idPelatih;
          });
          item[col.targetPayloadName || "target_map"] = targetMap;
          item[col.pelatihPayloadName || "pelatih_map"] = pelatihMap;
        }
      });
      teamColumns.forEach((col) => {
        const values = Array.from(latestMulti[`${id}:${col.multiColumnName}`] || []);
        const timMap = {};
        values.forEach((nomorID) => {
          const idTim = latestTeams[`${id}:${col.multiColumnName}:${nomorID}`];
          if (idTim) timMap[nomorID] = idTim;
        });
        item[col.payloadName || col.name] = timMap;
      });
      return item;
    });
    const resp = await axiosFetch({
      method: "POST",
      url: saveUrl,
      auth: true,
      data: koniLabel
        ? { ids: newlySelected.map((id) => Number(id)), koni_sendiri_id: Number(koniSendiriID || 0) }
        : multiColumns.length > 0
          ? { items }
          : { ids: newlySelected },
    });
    if (resp?.data?.success === true) {
      await showMsg("Informasi", "Data berhasil disimpan.");
      onSaved?.();
      closeHandle?.();
      return;
    }
    await showMsg("Error", resp?.message || "Data gagal disimpan.");
  };

  const saveAddedItem = async (form) => {
    const resp = await axiosFetch({
      method: "POST",
      url: addConfig?.url,
      auth: true,
      data: form,
    });
    if (resp?.data?.success === true && resp?.data?.id_kontingen) {
      setSelected((current) => {
        const next = new Set(current);
        next.add(String(resp.data.id_kontingen));
        return next;
      });
    }
    return resp;
  };

  const addSuccess = () => {
    setAddOpen(false);
    loadPage();
  };

  const saveTeam = async () => {
    const nama = String(teamForm?.nama || "").trim();
    if (!nama) {
      await showMsg("Error", "Nama Tim wajib diisi.");
      return;
    }
    const resp = await axiosFetch({
      method: "POST",
      url: "/kejuaraan-admin/tim",
      auth: true,
      data: {
        id_cabor: teamForm?.idCabor,
        id_nomor_pertandingan: teamForm?.nomorID,
        nama,
      },
    });
    if (resp?.data?.success !== true) {
      await showMsg("Error", resp?.message || "Tim gagal disimpan.");
      return;
    }
    const idTim = String(resp?.data?.id_tim || "");
    const teamName = String(resp?.data?.nama || nama);
    const updateItemOptions = (item) => {
      let changed = false;
      const options = parseOptionList(item?.nomor_options).map((option) => {
        if (String(option?.id || "") !== String(teamForm?.nomorID)) return option;
        const currentTeams = Array.isArray(option?.team_options) ? option.team_options : [];
        changed = true;
        return {
          ...option,
          team_options: currentTeams.some((team) => String(team?.id || "") === idTim)
            ? currentTeams
            : [...currentTeams, { id: idTim, nama: teamName }],
        };
      });
      if (!changed) return item;
      return { ...item, nomor_options: JSON.stringify(options) };
    };
    setRows((current) => current.map(updateItemOptions));
    setOptionRows((current) => {
      const next = current.map(updateItemOptions);
      optionRowsRef.current = next;
      return next;
    });
    setMultiPicker((current) => current ? { ...current, item: updateItemOptions(current.item) } : current);
    const latestTeams = {
      ...(selectedTeamsRef.current || {}),
      [`${teamForm.rowID}:${teamForm.columnName}:${teamForm.nomorID}`]: idTim,
    };
    selectedTeamsRef.current = latestTeams;
    setSelectedTeams(latestTeams);
    setTeamForm(null);
    await showMsg("Informasi", "Tim berhasil ditambahkan.");
  };

  return (
    <PopupDialog
      title={title}
      size="xl"
      show={show}
      closeHandle={closeHandle}
      closeLabel="Batal"
      doHandle={save}
      doLabel={loading ? "Menyimpan..." : "Simpan"}
      doDisabled={loading}
    >
      <div className="row mb-3">
        <div className="col-md-4">
          <input
            className="form-control"
            placeholder="Pencarian..."
            value={keyword}
            onChange={(event) => {
              setKeyword(event.target.value);
              setPage(1);
            }}
          />
        </div>
        {filters.map((filter) => (
          <div className="col-md-3" key={filter.name}>
            <select
              className="form-control"
              value={filterValue[filter.name] || ""}
              onChange={(event) => {
                setFilterValue((current) => ({ ...current, [filter.name]: event.target.value }));
                setPage(1);
              }}
            >
              <option value="">{filter.placeholder || "-Semua-"}</option>
              {(filter.options || []).map((item) => (
                <option key={item?.[filter.valueId]} value={item?.[filter.valueId]}>
                  {item?.[filter.labelId]}
                </option>
              ))}
            </select>
          </div>
        ))}
        {addConfig && (
          <div className="col-md-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setAddOpen(true)}
            >
              <i className="fa fa-plus mr-1" />
              {addConfig?.buttonLabel || "Tambah"}
            </button>
          </div>
        )}
      </div>
      <div className="dataTables_wrapper dt-bootstrap4">
        <div className="row">
          <div className="col-12 table-responsive">
            <table className="table sortable table-bordered table-hover table-striped table-condensed">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 70 }}>Pilih</th>
                  {koniLabel && <th className="text-center" style={{ width: 110 }}>{koniLabel}</th>}
                  {columns.map((col) => (
                    <th
                      key={col.name}
                      className={`${col.center ? "text-center" : ""} ${sortClass(col)}`.trim()}
                      style={{ ...checklistColumnStyle(col), cursor: col?.sortable === false || ["multi_select", "team_select"].includes(col?.type) ? "default" : "pointer" }}
                      onClick={() => sortData(col)}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length < 1 ? (
                  <tr>
                    <td colSpan={columns.length + (koniLabel ? 2 : 1)} className="text-center text-muted">Data tidak tersedia</td>
                  </tr>
                ) : (
                  rows.map((item) => (
                    <tr key={item.id}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(String(item.id))}
                          disabled={lockedSelected.has(String(item.id))}
                          onChange={() => toggleID(item.id)}
                        />
                      </td>
                      {koniLabel && (
                        <td className="text-center">
                          <input
                            type="radio"
                            name="koni_sendiri_id"
                            checked={koniSendiriID === String(item.id)}
                            disabled={lockedSelected.has(String(item.id))}
                            onChange={() => chooseKoniSendiri(item.id)}
                          />
                        </td>
                      )}
                      {columns.map((col) => (
                        <td key={col.name} className={col.center ? "text-center" : ""} style={checklistColumnStyle(col)}>
                          {col.type === "multi_select"
                            ? renderMultiButton(item, col)
                            : col.type === "team_select"
                              ? renderTeamSelect(item, col)
                              : showEmpty(col.render ? col.render(item) : item?.[col.name])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <DataPagination paging={paging} changePage={setPage} />
      </div>
      {addConfig && (
        <PopupDialog
          title={addConfig?.title || "Tambah Data"}
          size={addConfig?.size || "lg"}
          show={addOpen}
          closeHandle={() => setAddOpen(false)}
          allowClose={false}
        >
          <MyForm
            {...addConfig}
            title=""
            saveHandle={addSaveHandle || saveAddedItem}
            onSuccess={addSuccess}
            onCancel={() => setAddOpen(false)}
          />
        </PopupDialog>
      )}
      {multiPicker ? (
        <PopupDialog
          title={`${multiPicker.col.label} - ${multiPicker.item?.nama || ""}`}
          size="lg"
          show={Boolean(multiPicker)}
          closeHandle={closeMultiPicker}
          closeLabel="Tutup"
        >
          {(() => {
            const multiPerPage = 10;
	            const nomorOptions = parseOptionList(multiPicker.item?.[multiPicker.col.optionsField]);
	            const hasKategori = nomorOptions.some((option) => option?.kategori);
	            const hasTargetPelatih = Boolean(multiPicker.col.enableTargetPelatih);
            const keywordText = multiKeyword.trim().toLowerCase();
            const filteredOptions = keywordText
              ? nomorOptions.filter((option) => [option?.label, option?.kategori].some((value) => String(value || "").toLowerCase().includes(keywordText)))
              : nomorOptions;
            const totalPage = Math.max(1, Math.ceil(filteredOptions.length / multiPerPage));
            const currentPage = Math.min(Number(multiPage) || 1, totalPage);
            const pageOptions = filteredOptions.slice((currentPage - 1) * multiPerPage, currentPage * multiPerPage);
            const multiPaging = {
              count: filteredOptions.length,
              page: currentPage,
              total_page: totalPage,
            };
            return (
              <div className="dataTables_wrapper dt-bootstrap4">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <input
                      className="form-control"
                      placeholder="Pencarian..."
                      value={multiKeyword}
                      onChange={(event) => {
                        setMultiKeyword(event.target.value);
                        setMultiPage(1);
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 table-responsive">
                    <table className="table sortable table-bordered table-hover table-striped table-condensed mb-0">
                      <thead>
	                        <tr>
	                          <th className="text-center" style={{ width: 70 }}>Pilih</th>
	                          <th>{multiPicker.col.label}</th>
	                          {hasKategori ? <th>Kategori</th> : null}
	                          {hasTargetPelatih ? <th style={{ width: 130 }}>Target</th> : null}
	                          {hasTargetPelatih ? <th style={{ width: 210 }}>Pelatih</th> : null}
	                        </tr>
                      </thead>
                      <tbody>
                        {pageOptions.length < 1 ? (
                          <tr>
	                            <td colSpan={(hasKategori ? 3 : 2) + (hasTargetPelatih ? 2 : 0)} className="text-center text-muted">Data tidak tersedia</td>
                          </tr>
                        ) : (
                          pageOptions.map((option) => {
	                            const rowID = String(multiPicker.item.id);
	                            const stateKey = `${rowID}:${multiPicker.col.name}`;
	                            const optionID = String(option?.id || "");
	                            const isChecked = (selectedMulti[stateKey] || new Set()).has(optionID);
	                            const targetKey = `${rowID}:${multiPicker.col.name}:${optionID}`;
	                            const pelatihOptions = Array.isArray(option?.pelatih_options) ? option.pelatih_options : [];
	                            return (
	                              <tr key={optionID}>
	                                <td className="text-center">
	                                  <input
	                                    type="checkbox"
	                                    checked={isChecked}
	                                    onChange={() => toggleMultiValue(rowID, multiPicker.col.name, optionID)}
	                                  />
	                                </td>
	                                <td>{showEmpty(option?.label)}</td>
	                                {hasKategori ? <td>{showEmpty(option?.kategori)}</td> : null}
	                                {hasTargetPelatih ? (
	                                  <td>
	                                    <select
	                                      className="form-control form-control-sm"
	                                      value={selectedTargets[targetKey] || ""}
	                                      disabled={!isChecked}
	                                      onChange={(event) => {
	                                        const latest = { ...(selectedTargetsRef.current || {}) };
	                                        if (event.target.value) latest[targetKey] = event.target.value;
	                                        else delete latest[targetKey];
	                                        selectedTargetsRef.current = latest;
	                                        setSelectedTargets(latest);
	                                      }}
	                                    >
	                                      <option value="">Tak Ada</option>
	                                      {(multiPicker.col.medaliOptions || []).map((item) => (
	                                        <option key={item?.id_jenis_medali} value={item?.id_jenis_medali}>{item?.nama}</option>
	                                      ))}
	                                    </select>
	                                  </td>
	                                ) : null}
	                                {hasTargetPelatih ? (
	                                  <td>
	                                    <select
	                                      className="form-control form-control-sm"
	                                      value={selectedPelatihs[targetKey] || ""}
	                                      disabled={!isChecked}
	                                      onChange={(event) => {
	                                        const latest = { ...(selectedPelatihsRef.current || {}) };
	                                        if (event.target.value) latest[targetKey] = event.target.value;
	                                        else delete latest[targetKey];
	                                        selectedPelatihsRef.current = latest;
	                                        setSelectedPelatihs(latest);
	                                      }}
	                                    >
	                                      <option value="">-</option>
	                                      {pelatihOptions.map((item) => (
	                                        <option key={item?.id} value={item?.id}>{item?.nama}</option>
	                                      ))}
	                                    </select>
	                                  </td>
	                                ) : null}
	                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <DataPagination paging={multiPaging} changePage={setMultiPage} />
              </div>
            );
          })()}
        </PopupDialog>
      ) : null}
      {teamForm ? (
        <PopupDialog
          title="Tambah Tim"
          size="md"
          show={Boolean(teamForm)}
          closeHandle={() => setTeamForm(null)}
          closeLabel="Batal"
          doHandle={saveTeam}
          doLabel="Simpan"
          doDisabled={loading}
        >
          <div className="form-group">
            <label>Nomor Pertandingan</label>
            <input className="form-control" value={teamForm?.nomorLabel || ""} disabled />
          </div>
          <div className="form-group mb-0">
            <label>Nama Tim</label>
            <input
              className="form-control"
              value={teamForm?.nama || ""}
              onChange={(event) => setTeamForm((current) => ({ ...current, nama: event.target.value }))}
              placeholder="Nama Tim"
            />
          </div>
        </PopupDialog>
      ) : null}
    </PopupDialog>
  );
}

export function KejuaraanAdminKontingenPage() {
  const { user } = useAuth();
  const [contextResp] = useAxios({ method: "GET", url: "/kejuaraan-admin/context", auth: true });
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const isOperatorCabor = String(user?.groupname || "").toUpperCase() === "OPERATOR_KEJUARAAN_CABOR";
  const listColumns = ["no", "kode", "kontingen_name", "is_koni_sendiri", ...(!isOperatorCabor ? ["action"] : [])];
  const koniLabel = contextResp?.data?.koni_name
    ? `Kontingen ${contextResp.data.koni_name}`
    : "Kontingen KONI Saya";
  const coltype = {
    no: compactNoCol,
    id_kejuaraan_kontingen: { type: "hidden", name: "id_kejuaraan_kontingen" },
    kode: { label: "Kode", renderFunc: (val) => showEmpty(val), width: 80, align: "center" },
    kontingen_name: { label: "Kontingen", renderFunc: (val) => showEmpty(val), width: 320 },
    is_koni_sendiri: {
      label: koniLabel,
      type: "checkbox",
      name: "is_koni_sendiri",
      renderFunc: (val) => (parseBoolean(val) ? "Ya" : "Tidak"),
      renderExportFunc: (val) => (parseBoolean(val) ? "Ya" : "Tidak"),
      colsize: 6,
      width: 130,
      align: "center",
    },
    action: compactActionCol,
  };
  return (
    <>
    <DataList
      title="Kontingen"
      coltype={coltype}
      reload={reloadKey}
      toolbar={!isOperatorCabor ? [{ label: "Pilih Kontingen", icon: "fa-check-square", onClick: () => setBulkOpen(true) }] : []}
      dlist={{
        url: "/kejuaraan-admin/kontingen",
        columns: listColumns,
        filter: [{ dtype: "q", name: "q", colsize: 3 }],
        sort_by: ["kode", "kontingen_name"],
      }}
      delconfig={!isOperatorCabor ? { url: "/kejuaraan-admin/kontingen", cellid: "id_kejuaraan_kontingen", cellname: "kontingen_name", isAllow: allowDeleteWhenSafe } : null}
      pdflist={{ title: "Kontingen", columns: ["no", "kode", "kontingen_name", "is_koni_sendiri"] }}
      xlslist={{ title: "Kontingen", columns: ["no", "kode", "kontingen_name", "is_koni_sendiri"] }}
    />
    {!isOperatorCabor ? <BulkChecklistDialog
      show={bulkOpen}
      title="Pilih Kontingen"
      optionsUrl="/kejuaraan-admin/kontingen-options"
      saveUrl="/kejuaraan-admin/kontingen/bulk"
      columns={[
        { name: "kode", label: "Kode", center: true, width: 90 },
        { name: "nama", label: "Kontingen", width: 420 },
      ]}
      addConfig={{
        ...kontingenAddConfig(),
        buttonLabel: "Tambah Kontingen",
      }}
      koniLabel={koniLabel}
      closeHandle={() => setBulkOpen(false)}
      onSaved={() => setReloadKey((current) => current + 1)}
    /> : null}
    </>
  );
}

function AtletNomorEditDialog({ show, item, rolePath, medaliOptions = [], closeHandle, onSaved }) {
  const [showMsg] = useMsgBox();
  const [, , loading, axiosFetch] = useAxiosFunction();
  const showMsgRef = React.useRef(showMsg);
  const [detail, setDetail] = React.useState(null);
  const [selectedNomor, setSelectedNomor] = React.useState(new Set());
  const [selectedTeams, setSelectedTeams] = React.useState({});
  const [selectedTargets, setSelectedTargets] = React.useState({});
  const [selectedPelatihs, setSelectedPelatihs] = React.useState({});
  const [keyword, setKeyword] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [teamForm, setTeamForm] = React.useState(null);
  const itemID = String(item?.id_kejuaraan_sdm || item?.id || "");

  React.useEffect(() => {
    showMsgRef.current = showMsg;
  }, [showMsg]);

  React.useEffect(() => {
    let active = true;
    if (!show) {
      setDetail(null);
      setSelectedNomor(new Set());
      setSelectedTeams({});
      setSelectedTargets({});
      setSelectedPelatihs({});
      setKeyword("");
      setPage(1);
      setTeamForm(null);
      return () => {
        active = false;
      };
    }
    setDetail({
      id_kejuaraan_sdm: itemID,
      id_keanggotaan: item?.id_keanggotaan || "",
      id_cabor: item?.id_cabor || "",
      personal_name: formatPersonName(item, ["personal_name", "nama"]),
      nomor_induk: item?.nomor_induk || "",
      cabor_name: item?.cabor_name || "",
    });
    setSelectedNomor(new Set());
    setSelectedTeams({});
    setSelectedTargets({});
    setSelectedPelatihs({});
    setKeyword("");
    setPage(1);
    setTeamForm(null);
    if (!itemID) {
      return () => {
        active = false;
      };
    }
    (async () => {
      const resp = await axiosFetch({
        method: "GET",
        url: `/kejuaraan-admin/sdm/${rolePath}/${itemID}`,
        auth: true,
      });
      if (!active || isCanceledResponse(resp)) {
        return;
      }
      const payload = responsePayload(resp);
      if (!payload || payload?.success === false) {
        await showMsgRef.current("Error", resp?.message || "Data atlet gagal dimuat.");
        return;
      }
      setDetail(payload);
      setSelectedNomor(new Set(parseCSV(payload?.nomor_ids)));
      setSelectedTeams(parseObject(payload?.tim_map));
      setSelectedTargets(parseObject(payload?.target_map));
      setSelectedPelatihs(parseObject(payload?.pelatih_map));
    })();
    return () => {
      active = false;
    };
  }, [axiosFetch, itemID, item?.cabor_name, item?.id_cabor, item?.id_keanggotaan, item?.nama, item?.nomor_induk, item?.personal_name, rolePath, show]);

  const nomorOptions = parseOptionList(detail?.nomor_options);
  const filteredOptions = keyword.trim()
    ? nomorOptions.filter((option) => [option?.label, option?.kategori].some((value) => String(value || "").toLowerCase().includes(keyword.trim().toLowerCase())))
    : nomorOptions;
  const perPage = 10;
  const totalPage = Math.max(1, Math.ceil(filteredOptions.length / perPage));
  const currentPage = Math.min(Number(page) || 1, totalPage);
  const pageOptions = filteredOptions.slice((currentPage - 1) * perPage, currentPage * perPage);
  const paging = { count: filteredOptions.length, page: currentPage, total_page: totalPage };

  const toggleNomor = (idNomor) => {
    const key = String(idNomor || "");
    setSelectedNomor((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
        setSelectedTeams((teams) => {
          const updated = { ...teams };
          delete updated[key];
          return updated;
        });
        setSelectedTargets((targets) => {
          const updated = { ...targets };
          delete updated[key];
          return updated;
        });
        setSelectedPelatihs((pelatihs) => {
          const updated = { ...pelatihs };
          delete updated[key];
          return updated;
        });
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const updateItemOptions = (idTim, teamName) => {
    const updateDetail = (current) => {
      if (!current) return current;
      const options = parseOptionList(current.nomor_options).map((option) => {
        if (String(option?.id || "") !== String(teamForm?.nomorID)) return option;
        const currentTeams = Array.isArray(option?.team_options) ? option.team_options : [];
        return {
          ...option,
          team_options: currentTeams.some((team) => String(team?.id || "") === String(idTim))
            ? currentTeams
            : [...currentTeams, { id: idTim, nama: teamName }],
        };
      });
      return { ...current, nomor_options: JSON.stringify(options) };
    };
    setDetail(updateDetail);
  };

  const saveTeam = async () => {
    const nama = String(teamForm?.nama || "").trim();
    if (!nama) {
      await showMsg("Error", "Nama Tim wajib diisi.");
      return;
    }
    const resp = await axiosFetch({
      method: "POST",
      url: "/kejuaraan-admin/tim",
      auth: true,
      data: {
        id_cabor: detail?.id_cabor,
        id_nomor_pertandingan: teamForm?.nomorID,
        nama,
      },
    });
    if (resp?.data?.success !== true) {
      await showMsg("Error", resp?.message || "Tim gagal disimpan.");
      return;
    }
    const idTim = String(resp?.data?.id_tim || "");
    const teamName = String(resp?.data?.nama || nama);
    updateItemOptions(idTim, teamName);
    setSelectedTeams((current) => ({ ...current, [teamForm.nomorID]: idTim }));
    setTeamForm(null);
    await showMsg("Informasi", "Tim berhasil ditambahkan.");
  };

  const save = async () => {
    const nomorIDs = Array.from(selectedNomor);
    if (nomorIDs.length < 1) {
      await showMsg("Error", "Nomor Pertandingan wajib diisi.");
      return;
    }
    const timMap = {};
    const targetMap = {};
    const pelatihMap = {};
    for (const idNomor of nomorIDs) {
      const nomor = nomorOptions.find((option) => String(option?.id || "") === String(idNomor));
      if (!isNomorPerorangan(nomor)) {
        if (!selectedTeams[idNomor]) {
          await showMsg("Error", "Tim wajib diisi.");
          return;
        }
        timMap[idNomor] = selectedTeams[idNomor];
      }
      if (selectedTargets[idNomor]) targetMap[idNomor] = Number(selectedTargets[idNomor]);
      if (selectedPelatihs[idNomor]) pelatihMap[idNomor] = selectedPelatihs[idNomor];
    }
    const resp = await axiosFetch({
      method: "PUT",
      url: `/kejuaraan-admin/sdm/${rolePath}/${itemID}`,
      auth: true,
      data: {
        id_keanggotaan: detail?.id_keanggotaan,
        nomor_ids: nomorIDs,
        tim_map: timMap,
        target_map: targetMap,
        pelatih_map: pelatihMap,
      },
    });
    if (resp?.data?.success !== true) {
      await showMsg("Error", resp?.message || "Data atlet gagal disimpan.");
      return;
    }
    await showMsg("Informasi", "Data atlet berhasil disimpan.");
    onSaved?.();
    closeHandle?.();
  };

  return (
    <>
      <PopupDialog
        title={`Edit Nomor Pertandingan - ${formatPersonName(detail || item, ["personal_name"])}`}
        size="xl"
        show={show}
        closeHandle={closeHandle}
        closeLabel="Batal"
        doHandle={save}
        doLabel={loading ? "Menyimpan..." : "Simpan"}
        doDisabled={loading || !detail}
      >
        <div className="row mb-3">
          <div className="col-md-4">
            <label>Nama</label>
            <input className="form-control" value={formatPersonName(detail, ["personal_name"])} disabled />
          </div>
          <div className="col-md-3">
            <label>No. Induk</label>
            <input className="form-control" value={detail?.nomor_induk || ""} disabled />
          </div>
          <div className="col-md-5">
            <label>Cabor</label>
            <input className="form-control" value={detail?.cabor_name || ""} disabled />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Pencarian nomor pertandingan..."
              value={keyword}
              onChange={(event) => {
                setKeyword(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
        <div className="dataTables_wrapper dt-bootstrap4">
          <div className="row">
            <div className="col-12 table-responsive">
              <table className="table sortable table-bordered table-hover table-striped table-condensed mb-0">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: 70 }}>Pilih</th>
                    <th>Nomor Pertandingan</th>
                    <th style={{ width: 170 }}>Kategori</th>
                    <th style={{ width: 140 }}>Target</th>
                    <th style={{ width: 230 }}>Pelatih</th>
                    <th style={{ width: 260 }}>Tim</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOptions.length < 1 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">Data tidak tersedia</td>
                    </tr>
                  ) : pageOptions.map((nomor) => {
                    const idNomor = String(nomor?.id || "");
                    const checked = selectedNomor.has(idNomor);
                    const teamOptions = Array.isArray(nomor?.team_options) ? nomor.team_options : [];
                    const pelatihOptions = Array.isArray(nomor?.pelatih_options) ? nomor.pelatih_options : [];
                    const isPerorangan = isNomorPerorangan(nomor);
                    return (
                      <tr key={idNomor}>
                        <td className="text-center">
                          <input type="checkbox" checked={checked} onChange={() => toggleNomor(idNomor)} />
                        </td>
                        <td>{showEmpty(nomor?.label)}</td>
                        <td>{showEmpty(nomor?.kategori)}</td>
                        <td>
                          <select
                            className="form-control form-control-sm"
                            value={selectedTargets[idNomor] || ""}
                            disabled={!checked}
                            onChange={(event) => {
                              const value = event.target.value;
                              setSelectedTargets((current) => {
                                const next = { ...current };
                                if (value) next[idNomor] = value;
                                else delete next[idNomor];
                                return next;
                              });
                            }}
                          >
                            <option value="">Tak Ada</option>
                            {medaliOptions.map((medali) => (
                              <option key={medali?.id_jenis_medali} value={medali?.id_jenis_medali}>{medali?.nama}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            className="form-control form-control-sm"
                            value={selectedPelatihs[idNomor] || ""}
                            disabled={!checked}
                            onChange={(event) => {
                              const value = event.target.value;
                              setSelectedPelatihs((current) => {
                                const next = { ...current };
                                if (value) next[idNomor] = value;
                                else delete next[idNomor];
                                return next;
                              });
                            }}
                          >
                            <option value="">-</option>
                            {pelatihOptions.map((pelatih) => (
                              <option key={pelatih?.id} value={pelatih?.id}>{pelatih?.nama}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {isPerorangan ? "-" : (
                            <select
                              className="form-control form-control-sm"
                              value={selectedTeams[idNomor] || ""}
                              disabled={!checked}
                              onChange={(event) => {
                                const value = event.target.value;
                                if (value === "__ADD_TEAM__") {
                                  setTeamForm({ nomorID: idNomor, nomorLabel: nomor?.label || "", nama: "" });
                                  return;
                                }
                                setSelectedTeams((current) => {
                                  const next = { ...current };
                                  if (value) next[idNomor] = value;
                                  else delete next[idNomor];
                                  return next;
                                });
                              }}
                            >
                              <option value="">{nomor?.label ? `-Pilih Tim ${nomor.label}-` : "-Pilih Tim-"}</option>
                              {teamOptions.map((team) => (
                                <option key={team?.id} value={team?.id}>{getTeamLabel(team, nomor)}</option>
                              ))}
                              <option value="__ADD_TEAM__">Lainnya</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DataPagination paging={paging} changePage={setPage} />
        </div>
      </PopupDialog>
      {teamForm ? (
        <PopupDialog
          title="Tambah Tim"
          size="md"
          show={Boolean(teamForm)}
          closeHandle={() => setTeamForm(null)}
          closeLabel="Batal"
          doHandle={saveTeam}
          doLabel="Simpan"
          doDisabled={loading}
        >
          <div className="form-group">
            <label>Nomor Pertandingan</label>
            <input className="form-control" value={teamForm?.nomorLabel || ""} disabled />
          </div>
          <div className="form-group mb-0">
            <label>Nama Tim</label>
            <input
              className="form-control"
              value={teamForm?.nama || ""}
              onChange={(event) => setTeamForm((current) => ({ ...current, nama: event.target.value }))}
            />
          </div>
        </PopupDialog>
      ) : null}
    </>
  );
}

export function KejuaraanAdminCaborPage() {
  const { user } = useAuth();
  const [caborResp] = useAxios({ method: "GET", url: "/references/kejuaraan-admin-cabor", auth: true });
  const [kategoriResp] = useAxios({ method: "GET", url: "/references/kategori-prestasi", auth: true });
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const isOperatorCabor = String(user?.groupname || "").toUpperCase() === "OPERATOR_KEJUARAAN_CABOR";
  const caborOptions = toArray(caborResp);
  const kategoriOptions = toArray(kategoriResp);
  const listColumns = ["no", "cabor_name", "jml_nomor", "jml_nomor_diikuti", "jml_nomor_tak_diikuti", "jml_atlet", "jml_pelatih", "jml_wasit", "jml_juri", "jml_manajer", "jml_mekanik", "jml_official", ...(!isOperatorCabor ? ["action"] : [])];
  const jumlahColumns = ["jml_nomor", "jml_nomor_diikuti", "jml_nomor_tak_diikuti", "jml_atlet", "jml_pelatih", "jml_wasit", "jml_juri", "jml_manajer", "jml_mekanik", "jml_official"];
  const jumlahHeaderGroups = [{ label: "Jumlah", columns: jumlahColumns }];
  const coltype = {
    no: compactNoCol,
    id_kejuaraan_cabor: { type: "hidden", name: "id_kejuaraan_cabor" },
    id_cabor: { label: "Cabor", type: "dropdown", name: "id_cabor", colsize: 12, validation: RuleRequired, options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Pilih Cabor-", width: 220 },
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 240, pdfwidth: 50 },
    id_kategori_prestasi: { label: "Kategori", type: "dropdown", name: "id_kategori_prestasi", options: kategoriOptions, valueId: "id_kategori_prestasi", labelId: "nama", placeholder: "-Semua Kategori-" },
    kategori_prestasi_name: { label: "Kategori", renderFunc: (val) => showEmpty(val), width: 120 },
    jml_nomor: { label: "Jml Nomor", headerLabel: "Nomor", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 62, align: "center", pdfwidth: 5 },
    jml_nomor_diikuti: { label: "Jml Nomor Diikuti", headerLabel: "Diikuti", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 62, align: "center", pdfwidth: 5 },
    jml_nomor_tak_diikuti: { label: "Jml Nomor Tak Diikuti", headerLabel: "Tak Diikuti", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 78, align: "center", pdfwidth: 5 },
    jml_atlet: { label: "Jml Atlet", headerLabel: "Atlet", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 58, align: "center", pdfwidth: 5 },
    jml_pelatih: { label: "Jml Pelatih", headerLabel: "Pelatih", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 62, align: "center", pdfwidth: 5 },
    jml_wasit: { label: "Jml Wasit", headerLabel: "Wasit", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 58, align: "center", pdfwidth: 5 },
    jml_juri: { label: "Jml Juri", headerLabel: "Juri", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 54, align: "center", pdfwidth: 5 },
    jml_manajer: { label: "Jml Manajer", headerLabel: "Manajer", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 68, align: "center", pdfwidth: 5 },
    jml_mekanik: { label: "Jml Mekanik", headerLabel: "Mekanik", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 68, align: "center", pdfwidth: 5 },
    jml_official: { label: "Jml Official", headerLabel: "Official", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 68, align: "center", pdfwidth: 5 },
    action: compactActionCol,
  };
  return (
    <>
    <DataList
      title="Cabor"
      coltype={coltype}
      reload={reloadKey}
      toolbar={!isOperatorCabor ? [{ label: "Pilih Cabor", icon: "fa-check-square", onClick: () => setBulkOpen(true) }] : []}
      dlist={{ url: "/kejuaraan-admin/cabor", columns: listColumns, headerGroups: jumlahHeaderGroups, filter: [{ dtype: "q", name: "q", colsize: 3 }, { dtype: "dropdown", name: "id_kategori_prestasi", label: "Kategori", colsize: 3, options: kategoriOptions, valueId: "id_kategori_prestasi", labelId: "nama", placeholder: "-Semua Kategori-" }], sort_by: ["cabor_name"] }}
      delconfig={!isOperatorCabor ? { url: "/kejuaraan-admin/cabor", cellid: "id_kejuaraan_cabor", cellname: "cabor_name", isAllow: allowDeleteWhenSafe } : null}
      pdflist={{ title: "Cabor Kejuaraan", columns: ["no", "cabor_name", ...jumlahColumns], headerGroups: jumlahHeaderGroups }}
      xlslist={{ title: "Cabor Kejuaraan", columns: ["no", "cabor_name", ...jumlahColumns], headerGroups: jumlahHeaderGroups }}
      pdfconfig={{ orientation: "landscape" }}
    />
    {!isOperatorCabor ? <BulkChecklistDialog
      show={bulkOpen}
      title="Pilih Cabor"
      optionsUrl="/kejuaraan-admin/cabor-options"
      saveUrl="/kejuaraan-admin/cabor/bulk"
      columns={[{ name: "nama", label: "Cabor" }]}
      filters={[{ name: "id_kategori_prestasi", placeholder: "-Semua Kategori-", options: kategoriOptions, valueId: "id_kategori_prestasi", labelId: "nama" }]}
      closeHandle={() => setBulkOpen(false)}
      onSaved={() => setReloadKey((current) => current + 1)}
    /> : null}
    </>
  );
}

export function KejuaraanAdminNomorPage() {
  const [caborResp] = useAxios({ method: "GET", url: "/references/kejuaraan-admin-cabor-selected", auth: true });
  const [kategoriResp] = useAxios({ method: "GET", url: "/references/nomor-kategori", auth: true });
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const caborOptions = toArray(caborResp);
  const kategoriOptions = toArray(kategoriResp);
  const coltype = {
    no: compactNoCol,
    id_kejuaraan_nomor: { type: "hidden", name: "id_kejuaraan_nomor" },
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 170, pdfwidth: 25 },
    nomor_pertandingan_name: { label: "Nomor Pertandingan", renderFunc: (val) => showEmpty(val), width: 240, pdfwidth: 35 },
    id_nomor_kategori: { label: "Kategori", type: "dropdown", name: "id_nomor_kategori", options: kategoriOptions, valueId: "id_nomor_kategori", labelId: "nama", placeholder: "-Semua Kategori-" },
    nomor_kategori_name: { label: "Kategori", renderFunc: (val) => showEmpty(val), width: 150, pdfwidth: 25 },
    jml_atlet: { label: "Jml Atlet", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 85, align: "center", pdfwidth: 10 },
    action: compactActionCol,
  };
  return (
    <>
    <DataList
      title="Nomor Pertandingan"
      coltype={coltype}
      reload={reloadKey}
      toolbar={[{ label: "Pilih Nomor Pertandingan", icon: "fa-check-square", onClick: () => setBulkOpen(true) }]}
      dlist={{ url: "/kejuaraan-admin/nomor-pertandingan", columns: ["no", "cabor_name", "nomor_pertandingan_name", "nomor_kategori_name", "jml_atlet", "action"], filter: [{ dtype: "q", name: "q", colsize: 3 }, { dtype: "dropdown", name: "id_cabor", label: "Cabor", colsize: 3, options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" }, { dtype: "dropdown", name: "id_nomor_kategori", label: "Kategori", colsize: 3, options: kategoriOptions, valueId: "id_nomor_kategori", labelId: "nama", placeholder: "-Semua Kategori-" }], sort_by: ["cabor_name", "nomor_pertandingan_name", "nomor_kategori_name"] }}
      delconfig={{ url: "/kejuaraan-admin/nomor-pertandingan", cellid: "id_kejuaraan_nomor", cellname: "nomor_pertandingan_name", isAllow: allowDeleteWhenSafe }}
      pdflist={{ title: "Nomor Pertandingan Kejuaraan", columns: ["no", "cabor_name", "nomor_pertandingan_name", "nomor_kategori_name", "jml_atlet"] }}
      xlslist={{ title: "Nomor Pertandingan Kejuaraan", columns: ["no", "cabor_name", "nomor_pertandingan_name", "nomor_kategori_name", "jml_atlet"] }}
    />
    <BulkChecklistDialog
      show={bulkOpen}
      title="Pilih Nomor Pertandingan"
      optionsUrl="/kejuaraan-admin/nomor-pertandingan-options"
      saveUrl="/kejuaraan-admin/nomor-pertandingan/bulk"
      columns={[
        { name: "cabor_name", label: "Cabor" },
        { name: "nama", label: "Nomor Pertandingan" },
        { name: "nomor_kategori_name", label: "Kategori" },
      ]}
      filters={[{ name: "id_cabor", placeholder: "-Semua Cabor-", options: caborOptions, valueId: "id_cabor", labelId: "nama" }, { name: "id_nomor_kategori", placeholder: "-Semua Kategori-", options: kategoriOptions, valueId: "id_nomor_kategori", labelId: "nama" }]}
      closeHandle={() => setBulkOpen(false)}
      onSaved={() => setReloadKey((current) => current + 1)}
    />
    </>
  );
}

export function KejuaraanAdminSDMPage({ title, rolePath }) {
  const [caborResp] = useAxios({ method: "GET", url: "/references/kejuaraan-admin-cabor-selected", auth: true });
  const [medaliResp] = useAxios({ method: "GET", url: "/references/medali", auth: true });
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [binaanOpen, setBinaanOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedPelatih, setSelectedPelatih] = React.useState(null);
  const [selectedAtlet, setSelectedAtlet] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const caborOptions = toArray(caborResp);
  const medaliOptions = toArray(medaliResp);
  const isAtlet = String(rolePath || "").toUpperCase() === "ATLET";
  const isPelatih = String(rolePath || "").toUpperCase() === "PELATIH";
  const renderMultiline = (val) => <span style={{ whiteSpace: "pre-line" }}>{showEmpty(val, "-")}</span>;
  const formatBinaan = (item) => {
    const atlet = Number(item?.jml_binaan_atlet || 0);
    const tim = Number(item?.jml_binaan_tim || 0);
    return [
      atlet > 0 ? `${atlet} Atlet` : "",
      tim > 0 ? `${tim} Tim` : "",
    ].filter(Boolean).join("\n") || "-";
  };
  const openBinaan = (item) => {
    setSelectedPelatih(item);
    setBinaanOpen(true);
  };
  const openEditAtlet = (item) => {
    setSelectedAtlet(item);
    setEditOpen(true);
  };
  const listColumns = isAtlet
    ? ["no", "nik", "nomor_induk", "personal_name", "jenis_kelamin_code", "cabor_name", "jml_nomor_diikuti_atlet", "nomor_pertandingan_names", "action"]
    : ["no", "nik", "nomor_induk", "personal_name", "jenis_kelamin_code", "cabor_name", ...(isPelatih ? ["jml_binaan"] : []), "action"];
  const exportColumns = isAtlet
    ? ["no", "nik", "nomor_induk", "personal_name", "jenis_kelamin_code", "cabor_name", "jml_nomor_diikuti_atlet", "nomor_pertandingan_names"]
    : ["no", "nik", "nomor_induk", "personal_name", "jenis_kelamin_code", "cabor_name", ...(isPelatih ? ["jml_binaan"] : [])];
  const checklistColumns = isAtlet
    ? [
      { name: "nik", label: "NIK", center: true, width: 140 },
      { name: "nomor_induk", label: "Nomor Induk", center: true },
      { name: "nama", label: "Nama" },
      { name: "cabor_name", label: "Cabor" },
      {
        name: "nomor_pertandingan",
        label: "Nomor Pertandingan",
        type: "multi_select",
        optionsField: "nomor_options",
        selectedField: "selected_nomor_ids",
        payloadName: "nomor_ids",
        required: true,
        enableTargetPelatih: true,
        selectedTargetField: "selected_target_map",
        selectedPelatihField: "selected_pelatih_map",
        targetPayloadName: "target_map",
        pelatihPayloadName: "pelatih_map",
        medaliOptions,
        width: 260,
        wrap: true,
      },
      {
        name: "tim",
        label: "Tim",
        type: "team_select",
        multiColumnName: "nomor_pertandingan",
        optionsField: "nomor_options",
        selectedField: "selected_tim_ids",
        payloadName: "tim_map",
        width: 260,
        wrap: true,
      },
    ]
    : [
      { name: "nik", label: "NIK", center: true, width: 140 },
      { name: "nomor_induk", label: "No. Induk", center: true },
      { name: "nama", label: "Nama" },
      { name: "cabor_name", label: "Cabor" },
    ];
  const coltype = {
    no: compactNoCol,
    id_kejuaraan_sdm: { type: "hidden", name: "id_kejuaraan_sdm" },
    personal_name: {
      label: "Nama",
      renderFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["personal_name"])),
      renderExportFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["personal_name"])),
      width: 220,
    },
    jenis_kelamin_code: { label: "JK", renderFunc: (val) => showEmpty(val), width: 42, align: "center", pdfwidth: 4 },
    nik: { label: "NIK", renderFunc: (val) => showEmpty(val), width: 100, align: "center" },
    nomor_induk: { label: "No. Induk", renderFunc: (val) => showEmpty(val), width: 95, align: "center" },
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 170 },
    organisasi_name: { label: "Organisasi", renderFunc: (val) => showEmpty(val), width: 170 },
    jml_nomor_diikuti_atlet: { label: "Jml Nomor", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 62, align: "center" },
    nomor_pertandingan_names: { label: "Nomor Pertandingan/Target", renderFunc: renderMultiline, renderExportFunc: (val) => showEmpty(val, "-"), width: 220 },
    jml_binaan: { label: "Jml Binaan", renderFunc: (_val, _col, item) => renderMultiline(formatBinaan(item)), renderExportFunc: (_val, _col, item) => formatBinaan(item), width: 85, align: "center", pdfwidth: 10 },
    action: isPelatih || isAtlet ? { ...compactActionCol, width: 72 } : compactActionCol,
  };
  const renderActionExtension = (isPelatih || isAtlet) ? (val, col, item) => (
    <>
      {isAtlet ? (
        <>
          <button type="button" className="btn btn-primary btn-xs" title="Edit Nomor Pertandingan" onClick={() => openEditAtlet(item)}>
            <i className="fa fa-pencil-alt" />
          </button>
          &nbsp;
        </>
      ) : null}
      {isPelatih ? (
        <>
          <button type="button" className="btn btn-info btn-xs" title="Atur Binaan" onClick={() => openBinaan(item)}>
            <i className="fa fa-users" />
          </button>
          &nbsp;
        </>
      ) : null}
    </>
  ) : undefined;
  return (
    <>
    <DataList
      title={title}
      coltype={coltype}
      reload={reloadKey}
      toolbar={[{ label: `Pilih ${title}`, icon: "fa-check-square", onClick: () => setBulkOpen(true) }]}
      dlist={{ url: `/kejuaraan-admin/sdm/${rolePath}`, columns: listColumns, filter: [{ dtype: "q", name: "q", colsize: 3 }, { dtype: "dropdown", name: "id_cabor", label: "Cabor", colsize: 3, options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" }], sort_by: ["personal_name", "cabor_name"], render_action_extension: renderActionExtension }}
      delconfig={{ url: `/kejuaraan-admin/sdm/${rolePath}`, cellid: "id_kejuaraan_sdm", cellname: "personal_name", isAllow: allowDeleteWhenSafe }}
      pdflist={{ title, columns: exportColumns }}
      xlslist={{ title, columns: exportColumns }}
      pdfconfig={isAtlet ? { orientation: "landscape" } : undefined}
    />
    <BulkChecklistDialog
      show={bulkOpen}
      title={`Pilih ${title}`}
      optionsUrl={`/kejuaraan-admin/sdm-options/${rolePath}`}
      saveUrl={`/kejuaraan-admin/sdm-bulk/${rolePath}`}
      columns={checklistColumns}
      filters={[{ name: "id_cabor", placeholder: "-Semua Cabor-", options: caborOptions, valueId: "id_cabor", labelId: "nama" }]}
      closeHandle={() => setBulkOpen(false)}
      onSaved={() => setReloadKey((current) => current + 1)}
    />
    {isPelatih ? <PelatihBinaanDialog
      show={binaanOpen}
      item={selectedPelatih}
      closeHandle={() => {
        setBinaanOpen(false);
        setSelectedPelatih(null);
      }}
      onSaved={() => setReloadKey((current) => current + 1)}
    /> : null}
    {isAtlet ? <AtletNomorEditDialog
      show={editOpen}
      item={selectedAtlet}
      rolePath={rolePath}
      medaliOptions={medaliOptions}
      closeHandle={() => {
        setEditOpen(false);
        setSelectedAtlet(null);
      }}
      onSaved={() => setReloadKey((current) => current + 1)}
    /> : null}
    </>
  );
}
